// planning-ng/src/app/components/ors-address-autocomplete/ors-address-autocomplete.component.ts
import { Component, OnInit, OnDestroy, forwardRef, signal, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  ReactiveFormsModule,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { HttpClient } from '@angular/common/http';
import { debounceTime, switchMap, catchError } from 'rxjs/operators';
import { of, Subject, takeUntil } from 'rxjs';
import { dataStore } from '../../app.component';

export interface OrsAddressSuggestion {
  formattedAddress: string;
  name?: string;
  street?: string;
  housenumber?: string;
  postalcode?: string;
  locality?: string;
  layer?: string;
}

@Component({
  selector: 'app-ors-address-autocomplete',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
  ],
  templateUrl: './ors-address-autocomplete.component.html',
  styleUrls: ['./ors-address-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrsAddressAutocompleteComponent),
      multi: true,
    },
  ],
})
export class OrsAddressAutocompleteComponent implements OnInit, OnDestroy, ControlValueAccessor {
  label = input('Adres');
  placeholder = input('Voer een adres in');
  required = input(false);
  appearance = input<'fill' | 'outline' | 'legacy' | 'standard'>('outline');
  autoActiveFirstOption = input(true);
  debounceMs = input(1000);
  country = input('NL');
  minSearchLength = input(3);

  addressSelected = output<string>();
  suggestionSelected = output<OrsAddressSuggestion>();
  error = output<string>();

  searchControl = new FormControl('');
  suggestions = signal<OrsAddressSuggestion[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  private destroy$ = new Subject<void>();
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.setupAutocomplete();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.searchControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  private setupAutocomplete(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.debounceMs()),
        switchMap((addressString: string) => {
          if (!addressString || addressString.length < this.minSearchLength()) {
            this.suggestions.set([]);
            this.isLoading.set(false);
            return of([]);
          }

          const apiKey = dataStore.generalSettings?.orsApiKey;
          if (!apiKey) {
            this.errorMessage.set('ORS API key niet geconfigureerd');
            this.error.emit(this.errorMessage());
            return of([]);
          }

          this.isLoading.set(true);
          this.errorMessage.set('');

          const url = `https://api.openrouteservice.org/geocode/autocomplete?api_key=${apiKey}&text=${encodeURIComponent(addressString)}&boundary.country=${this.country()}&layers=address,venue`;

          return this.http.get<any>(url).pipe(
            catchError((err) => {
              console.error('ORS API Error:', err);
              this.errorMessage.set(
                'Er is een fout opgetreden bij het ophalen van adressuggesties',
              );
              this.error.emit(this.errorMessage());
              this.isLoading.set(false);
              return of({ features: [] });
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.isLoading.set(false);
        this.suggestions.set(this.formatSuggestions(response?.features || []));
      });

    // Emit changes to parent form control
    this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(value || '');
    });
  }

  private formatSuggestions(features: any[]): OrsAddressSuggestion[] {
    const suggestionsArray: OrsAddressSuggestion[] = [];

    for (const feature of features) {
      const props = feature.properties;
      let formattedAddress: string;

      if (props.layer === 'venue') {
        formattedAddress =
          `${props.name ?? ''} ${props.street ?? ''} ${props.housenumber ?? ''}, ${props.postalcode ?? ''} ${props.locality}`.trim();
      } else {
        formattedAddress =
          `${props.street ?? props.name} ${props.housenumber ?? ''}, ${props.postalcode ?? ''} ${props.locality}`.trim();
      }

      // Clean up extra spaces
      formattedAddress = formattedAddress.replace(/\s+/g, ' ').trim();

      // Avoid duplicates
      if (!suggestionsArray.find((s) => s.formattedAddress === formattedAddress)) {
        suggestionsArray.push({
          formattedAddress,
          name: props.name,
          street: props.street,
          housenumber: props.housenumber,
          postalcode: props.postalcode,
          locality: props.locality,
          layer: props.layer,
        });
      }
    }

    return suggestionsArray;
  }

  onAddressSelected(address: string): void {
    this.onTouched();
    this.addressSelected.emit(address);

    const suggestion = this.suggestions().find((s) => s.formattedAddress === address);
    if (suggestion) {
      this.suggestionSelected.emit(suggestion);
    }
  }
}
